import React, { useState, useEffect, useRef } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart,
} from "recharts";
import GeographicMap from "./GeographicMap";

// Custom force simulation implementation
const useForceSimulation = (nodes, links, width, height) => {
  const [positions, setPositions] = useState({});

  useEffect(() => {
    // Initialize positions in a more spread out pattern
    const initialPositions = { ...positions };

    // First, position key nodes strategically
    const bootNodeId = nodes.find((n) => n.group === "bootnode")?.id;
    if (bootNodeId && !initialPositions[bootNodeId]) {
      // Place bootnode at center
      initialPositions[bootNodeId] = {
        x: width / 2,
        y: height / 2,
      };
    }

    // Place bucket nodes in a circle around the bootnode
    const bucketNodes = nodes.filter((n) => n.group === "bucket");
    const bucketCount = bucketNodes.length;

    if (bucketCount > 0) {
      // Calculate circle radius - use 30% of the smallest dimension
      const radius = Math.min(width, height) * 0.3;

      bucketNodes.forEach((node, index) => {
        if (!initialPositions[node.id]) {
          // Calculate position on circle
          const angle = (index / bucketCount) * 2 * Math.PI;
          initialPositions[node.id] = {
            x: width / 2 + radius * Math.cos(angle),
            y: height / 2 + radius * Math.sin(angle),
          };
        }
      });
    }

    // Now place remaining nodes with some randomness but near their connected nodes
    nodes.forEach((node) => {
      if (!initialPositions[node.id]) {
        // Find connected nodes that already have positions
        const connectedLinks = links.filter(
          (link) => link.source === node.id || link.target === node.id,
        );

        if (connectedLinks.length > 0) {
          // Get the first connected node that has a position
          const connectedNodeId =
            connectedLinks[0].source === node.id
              ? connectedLinks[0].target
              : connectedLinks[0].source;

          if (initialPositions[connectedNodeId]) {
            // Place near connected node with some randomness
            const basePos = initialPositions[connectedNodeId];
            // Higher radius means nodes are placed further from their connections
            const placementRadius = node.group === "bucket" ? 150 : 100;
            const angle = Math.random() * 2 * Math.PI;

            initialPositions[node.id] = {
              x: basePos.x + placementRadius * Math.cos(angle),
              y: basePos.y + placementRadius * Math.sin(angle),
            };
          } else {
            // Fallback to a wide spread random position
            initialPositions[node.id] = {
              x: width * 0.2 + Math.random() * width * 0.6,
              y: height * 0.2 + Math.random() * height * 0.6,
            };
          }
        } else {
          // No connections yet, place randomly but not at the edges
          initialPositions[node.id] = {
            x: width * 0.2 + Math.random() * width * 0.6,
            y: height * 0.2 + Math.random() * height * 0.6,
          };
        }
      }
    });

    // Simple force simulation with carefully tuned parameters for visual spacing
    let currentPositions = { ...initialPositions };
    const simulate = () => {
      // Apply very gentle boundary force to keep nodes in view
      nodes.forEach((node) => {
        const pos = currentPositions[node.id];

        // Keep nodes within the bounds with a margin
        const margin = 50;
        if (pos.x < margin) pos.x += 1;
        if (pos.x > width - margin) pos.x -= 1;
        if (pos.y < margin) pos.y += 1;
        if (pos.y > height - margin) pos.y -= 1;
      });

      // Apply node repulsion force - stronger for more spacing
      nodes.forEach((nodeA) => {
        nodes.forEach((nodeB) => {
          if (nodeA.id !== nodeB.id) {
            const posA = currentPositions[nodeA.id];
            const posB = currentPositions[nodeB.id];
            const dx = posB.x - posA.x;
            const dy = posB.y - posA.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            // Apply repulsion up to this distance - increased for more spacing
            const repulsionRadius = 250;

            if (distance > 0 && distance < repulsionRadius) {
              // Calculate repulsion force - stronger for closer nodes
              // The 25 factor controls overall repulsion strength
              const force = (1 - distance / repulsionRadius) * 25;

              // Calculate force components
              const forceX = (dx / distance) * force;
              const forceY = (dy / distance) * force;

              // Apply forces - move nodes away from each other
              posA.x -= forceX;
              posA.y -= forceY;
              posB.x += forceX;
              posB.y += forceY;
            }
          }
        });
      });

      // Apply link attraction force - gentle to maintain spacing
      links.forEach((link) => {
        const sourcePos = currentPositions[link.source];
        const targetPos = currentPositions[link.target];

        if (sourcePos && targetPos) {
          const dx = targetPos.x - sourcePos.x;
          const dy = targetPos.y - sourcePos.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          // Only apply attraction if nodes are far apart
          // This minimum distance helps maintain spacing
          const minDistance = 80;
          const maxDistance = 300;

          if (distance > maxDistance) {
            // Strong attraction for very distant nodes
            const force = (distance - maxDistance) * 0.05;
            const forceX = (dx / distance) * force;
            const forceY = (dy / distance) * force;

            sourcePos.x += forceX;
            sourcePos.y += forceY;
            targetPos.x -= forceX;
            targetPos.y -= forceY;
          } else if (distance > minDistance) {
            // Weaker attraction for nodes at moderate distances
            const force = (distance - minDistance) * 0.01;
            const forceX = (dx / distance) * force;
            const forceY = (dy / distance) * force;

            sourcePos.x += forceX;
            sourcePos.y += forceY;
            targetPos.x -= forceX;
            targetPos.y -= forceY;
          }
        }
      });

      setPositions({ ...currentPositions });
    };

    // Run fewer simulation steps initially
    for (let i = 0; i < 30; i++) {
      simulate();
    }

    // Continue simulation with animation at a slower rate
    const interval = setInterval(simulate, 100);
    return () => clearInterval(interval);
  }, [nodes, links, width, height]);

  return positions;
};

// New Component: Peer Type Distribution Chart
const PeerTypeDistribution = ({ peerDetails, colors }) => {
  // Calculate peer type distribution
  const calculatePeerTypeData = () => {
    if (!peerDetails || peerDetails.length === 0) {
      return [{ name: "No Data", value: 1, fill: colors.textSecondary }];
    }

    // Count peers by type
    const counts = {
      reachable: 0,
      unreachable: 0,
      knownPeer: 0,
    };

    peerDetails.forEach((peer) => {
      if (peer.inIplist) {
        counts.knownPeer++;
      } else if (peer.reachable) {
        counts.reachable++;
      } else {
        counts.unreachable++;
      }
    });

    // Generate data for the pie chart
    return [
      { name: "Known Peers", value: counts.knownPeer, fill: colors.knownPeer },
      {
        name: "Reachable Peers",
        value: counts.reachable,
        fill: colors.reachable,
      },
      {
        name: "Unreachable Peers",
        value: counts.unreachable,
        fill: colors.unreachable,
      },
    ].filter((entry) => entry.value > 0); // Only show non-zero entries
  };

  const data = calculatePeerTypeData();

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
    name,
    value,
  }) => {
    const radius = outerRadius * 1.1;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill={colors.text}
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize="12"
      >
        {`${name}: ${value} (${(percent * 100).toFixed(1)}%)`}
      </text>
    );
  };

  return (
    <div style={{ height: "280px" }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomizedLabel}
            outerRadius={80}
            innerRadius={45}
            fill="#8884d8"
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value) => [`${value} peers`, "Count"]}
            contentStyle={{
              backgroundColor: colors.cardBg,
              borderColor: colors.inputBg,
            }}
            itemStyle={{ color: colors.text }}
            labelStyle={{ color: colors.textSecondary }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

// New Component: Time Series Graph
const TimeSeriesGraph = ({ historicalData, colors }) => {
  if (!historicalData || historicalData.length <= 1) {
    return (
      <div className="h-64 flex items-center justify-center">
        <div style={{ color: colors.textSecondary }}>
          <svg
            className="w-10 h-10 mx-auto mb-2 opacity-50"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Waiting for data... graphs will appear after multiple polls
        </div>
      </div>
    );
  }

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div style={{ height: "280px" }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={historicalData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={colors.inputBg}
            opacity={0.3}
          />
          <XAxis
            dataKey="timestamp"
            tickFormatter={formatTimestamp}
            stroke={colors.textSecondary}
            tick={{ fill: colors.textSecondary }}
          />
          <YAxis
            yAxisId="left"
            stroke={colors.textSecondary}
            tick={{ fill: colors.textSecondary }}
            label={{
              value: "Peer Count",
              angle: -90,
              position: "insideLeft",
              style: { fill: colors.textSecondary },
            }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={[0, 100]}
            stroke={colors.textSecondary}
            tick={{ fill: colors.textSecondary }}
            label={{
              value: "Percentage",
              angle: 90,
              position: "insideRight",
              style: { fill: colors.textSecondary },
            }}
          />
          <Tooltip
            formatter={(value, name) => {
              if (name === "reachabilityPercentage") {
                return [`${value.toFixed(1)}%`, "Reachability %"];
              }
              return [
                value,
                name.charAt(0).toUpperCase() +
                  name.slice(1).replace(/([A-Z])/g, " $1"),
              ];
            }}
            labelFormatter={formatTimestamp}
            contentStyle={{
              backgroundColor: colors.cardBg,
              borderColor: colors.inputBg,
            }}
            itemStyle={{ color: colors.text }}
            labelStyle={{ color: colors.textSecondary }}
          />
          <Legend
            wrapperStyle={{ color: colors.text }}
            formatter={(value) => {
              return (
                value.charAt(0).toUpperCase() +
                value.slice(1).replace(/([A-Z])/g, " $1")
              );
            }}
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="totalPeers"
            name="totalPeers"
            stroke={colors.text}
            activeDot={{ r: 8 }}
            strokeWidth={2}
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="reachablePeers"
            name="reachablePeers"
            stroke={colors.reachable}
            activeDot={{ r: 8 }}
            strokeWidth={2}
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="unreachablePeers"
            name="unreachablePeers"
            stroke={colors.unreachable}
            activeDot={{ r: 8 }}
            strokeWidth={2}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="reachabilityPercentage"
            name="reachabilityPercentage"
            stroke={colors.highlight}
            activeDot={{ r: 8 }}
            strokeWidth={2}
            strokeDasharray="3 3"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// Regional Trends Component
const RegionalTrends = ({ peerDetails, historicalRegionalData, colors }) => {
  if (!historicalRegionalData || historicalRegionalData.length <= 1) {
    return (
      <div className="h-64 flex items-center justify-center">
        <div style={{ color: colors.textSecondary }}>
          <svg
            className="w-10 h-10 mx-auto mb-2 opacity-50"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Regional trends will appear after multiple polls
        </div>
      </div>
    );
  }

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Get the top 5 regions by peer count
  const getTopRegions = () => {
    const latestData =
      historicalRegionalData[historicalRegionalData.length - 1];
    if (!latestData || !latestData.regions) return [];

    return Object.keys(latestData.regions)
      .sort((a, b) => latestData.regions[b] - latestData.regions[a])
      .slice(0, 5);
  };

  const topRegions = getTopRegions();

  // Generate color palette for regions
  const regionColors = [
    colors.highlight,
    colors.knownPeer,
    colors.accent,
    colors.success,
    "#A78BFA", // Additional colors for regions
  ];

  return (
    <div style={{ height: "280px" }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={historicalRegionalData}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={colors.inputBg}
            opacity={0.3}
          />
          <XAxis
            dataKey="timestamp"
            tickFormatter={formatTimestamp}
            stroke={colors.textSecondary}
            tick={{ fill: colors.textSecondary }}
          />
          <YAxis
            stroke={colors.textSecondary}
            tick={{ fill: colors.textSecondary }}
          />
          <Tooltip
            formatter={(value, name) => [value, name]}
            labelFormatter={formatTimestamp}
            contentStyle={{
              backgroundColor: colors.cardBg,
              borderColor: colors.inputBg,
            }}
            itemStyle={{ color: colors.text }}
            labelStyle={{ color: colors.textSecondary }}
          />
          <Legend wrapperStyle={{ color: colors.text }} />
          {topRegions.map((region, index) => (
            <Area
              key={region}
              type="monotone"
              dataKey={`regions.${region}`}
              name={region}
              stroke={regionColors[index % regionColors.length]}
              fill={regionColors[index % regionColors.length]}
              fillOpacity={0.3}
              stackId="1"
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const P2PVisualizer = () => {
  // Studio Ghibli color palette
  const colors = {
    background: "#1D3557", // Deep blue background (Castle in the Sky inspired)
    cardBg: "#2A4365", // Darker blue for card backgrounds
    inputBg: "#355070", // Medium blue for inputs
    accent: "#4ECDC4", // Turquoise accent (Spirited Away inspired)
    highlight: "#F7B801", // Golden yellow (Totoro/Howl's Moving Castle inspired)
    success: "#9BC53D", // Soft green (Arrietty inspired)
    error: "#E63946", // Soft red (Princess Mononoke inspired)
    text: "#F1FAEE", // Off-white text
    textSecondary: "#A8DADC", // Light blue secondary text

    // Node colors
    bootnode: "#F7B801", // Golden yellow for bootnode
    bucket: "#4ECDC4", // Turquoise for buckets
    reachable: "#9BC53D", // Soft green for reachable peers
    unreachable: "#E63946", // Soft red for unreachable peers
    knownPeer: "#FF88CA", // Complementary pink for known peers
  };

  // State for metrics and visualization data
  const [metrics, setMetrics] = useState({
    knownPeers: 0,
    unreachablePeers: 0,
    lastPollTimestamp: 0,
    pollDuration: 0,
    pollCount: 0,
    pollSuccess: 0,
    peersPerBucket: [],
    activeBuckets: [],
    bootnodeInfo: null,
    peerDetails: [],
  });

  // New state for historical data tracking
  const [historicalData, setHistoricalData] = useState([]);
  const [historicalRegionalData, setHistoricalRegionalData] = useState([]);

  const [graphData, setGraphData] = useState({
    nodes: [],
    links: [],
  });

  // State for visualization mode
  const [visualizationMode, setVisualizationMode] = useState("network"); // 'network' or 'geographic'

  // Zoom and pan state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const svgRef = useRef(null);

  const pollIntervalRef = useRef(null);
  // Set default URL to make it easier for users
  const [prometheusUrl, setPrometheusUrl] = useState(
    "https://eth-map.deltaflyer.llc",
  );
  const [isPolling, setIsPolling] = useState(false);
  const [pollInterval, setPollInterval] = useState(300); // 5 minutes = 300 seconds
  const [error, setError] = useState(null);

  const graphWidth = 800;
  const graphHeight = 600;

  // Calculate positions for nodes
  const positions = useForceSimulation(
    graphData.nodes,
    graphData.links,
    graphWidth,
    graphHeight,
  );

  // Calculate viewBox based on zoom and pan
  const calculateViewBox = () => {
    const width = graphWidth / zoom;
    const height = graphHeight / zoom;
    const x = pan.x - width / 2;
    const y = pan.y - height / 2;
    return `${x} ${y} ${width} ${height}`;
  };

  // Zoom handlers
  const handleZoomIn = () => {
    setZoom((prevZoom) => Math.min(prevZoom * 1.2, 5)); // Limit max zoom
  };

  const handleZoomOut = () => {
    setZoom((prevZoom) => Math.max(prevZoom / 1.2, 0.5)); // Limit min zoom
  };

  const handleZoomReset = () => {
    setZoom(1);
    setPan({ x: graphWidth / 2, y: graphHeight / 2 });
  };

  // Mouse event handlers for panning
  const handleMouseDown = (e) => {
    if (e.button === 0) {
      // Left mouse button
      setIsDragging(true);
      setDragStart({
        x: e.clientX,
        y: e.clientY,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;

      setPan((prevPan) => ({
        x: prevPan.x - dx / zoom,
        y: prevPan.y - dy / zoom,
      }));

      setDragStart({
        x: e.clientX,
        y: e.clientY,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Mouse wheel handler for zooming
  const handleWheel = (e) => {
    e.preventDefault();
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.min(Math.max(zoom * zoomFactor, 0.5), 5); // Limit zoom range

    setZoom(newZoom);
  };

  // Initialize pan position
  useEffect(() => {
    setPan({ x: graphWidth / 2, y: graphHeight / 2 });
  }, [graphWidth, graphHeight]);

  // Improved function to parse Prometheus metrics text format
  const parsePrometheusMetrics = (text) => {
    const metrics = {};
    const peerDetails = [];
    let bootnodeInfo = null;

    try {
      if (!text || text.trim() === "") {
        console.warn("Empty metrics text received");
        return { metrics, peerDetails, bootnodeInfo };
      }

      const lines = text.split("\n");
      console.log(`Parsing ${lines.length} lines of metrics data`);

      lines.forEach((line) => {
        // Skip comments and empty lines
        if (line.startsWith("#") || line.trim() === "") return;

        // Parse bootnode info
        const bootnodeInfoMatch = line.match(
          /bootnode_info{id="([^"]+)",ip="([^"]+)"}\s+([0-9.e+-]+)/,
        );
        if (bootnodeInfoMatch) {
          bootnodeInfo = {
            id: bootnodeInfoMatch[1],
            ip: bootnodeInfoMatch[2],
          };
          return;
        }

        // Parse peer details with new fields
        const peerDetailMatch = line.match(
          /bootnode_peer_details{bucket="([^"]+)",first_seen_time="([^"]+)",in_iplist="([^"]+)",ip="([^"]+)",peer_id="([^"]+)",reachable="([^"]+)",times_reachable="([^"]+)",times_seen_in_scans="([^"]+)",times_unreachable="([^"]+)"}\s+([0-9.e+-]+)/,
        );
        if (peerDetailMatch) {
          peerDetails.push({
            bucket: peerDetailMatch[1],
            firstSeenTime: parseInt(peerDetailMatch[2]),
            inIplist: peerDetailMatch[3] === "1",
            ip: peerDetailMatch[4],
            peerId: peerDetailMatch[5],
            reachable: peerDetailMatch[6] === "1",
            timesReachable: parseInt(peerDetailMatch[7]),
            timesSeenInScans: parseInt(peerDetailMatch[8]),
            timesUnreachable: parseInt(peerDetailMatch[9]),
          });
          return;
        }

        // Parse simple metrics (no labels)
        const simpleMatch = line.match(/^([a-zA-Z0-9_]+)\s+([0-9.e+-]+)/);
        if (simpleMatch) {
          metrics[simpleMatch[1]] = parseFloat(simpleMatch[2]);
          console.log(
            `Parsed simple metric: ${simpleMatch[1]} = ${simpleMatch[2]}`,
          );
          return;
        }

        // Parse metrics with labels
        const labelMatch = line.match(/^([a-zA-Z0-9_]+){(.+)}\s+([0-9.e+-]+)/);
        if (labelMatch) {
          const name = labelMatch[1];
          const labels = labelMatch[2];
          const value = parseFloat(labelMatch[3]);

          if (!metrics[name]) metrics[name] = {};
          metrics[name][labels] = value;
          console.log(`Parsed labeled metric: ${name}{${labels}} = ${value}`);
        } else {
          console.log(`Could not parse line: ${line}`);
        }
      });

      console.log("Parsed metrics summary:", metrics);
      console.log(`Found ${peerDetails.length} peer details`);
      if (bootnodeInfo) {
        console.log("Found bootnode info:", bootnodeInfo);
      }
      return { metrics, peerDetails, bootnodeInfo };
    } catch (error) {
      console.error("Error parsing metrics:", error);
      return { metrics: {}, peerDetails: [], bootnodeInfo: null };
    }
  };

  // Improved function to parse bucket metrics specifically
  const parsePerBucketMetrics = (text) => {
    const bucketMetrics = [];

    try {
      if (!text || text.trim() === "") return bucketMetrics;

      const lines = text.split("\n");

      lines.forEach((line) => {
        const match = line.match(
          /bootnode_peers_per_bucket{bucket="([^"]+)"}\s+([0-9.e+-]+)/,
        );
        if (match) {
          const bucket = match[1];
          const count = parseFloat(match[2]);
          bucketMetrics.push({
            bucket: bucket,
            count: count,
          });
          console.log(`Parsed bucket metric: bucket=${bucket}, count=${count}`);
        }
      });

      console.log("Parsed bucket metrics:", bucketMetrics);
      return bucketMetrics;
    } catch (error) {
      console.error("Error parsing bucket metrics:", error);
      return [];
    }
  };

  // Function to fetch metrics from Prometheus
  const fetchMetrics = async () => {
    try {
      setError(null);
      // Use a more robust fetch with timeout and better error handling
      console.log(`Fetching metrics from: ${prometheusUrl}/metrics`);

      // Create an AbortController for the timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10-second timeout

      const response = await fetch(`${prometheusUrl}/metrics`, {
        method: "GET",
        mode: "cors", // Enable CORS
        credentials: "omit", // Don't send cookies
        signal: controller.signal,
        headers: {
          Accept: "text/plain",
          "Cache-Control": "no-cache",
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const text = await response.text();
      console.log(`Received metrics data length: ${text.length}`);
      console.log("Raw metrics text sample:", text.substring(0, 500) + "...");

      // Parse the metrics text format
      const {
        metrics: parsedMetrics,
        peerDetails,
        bootnodeInfo,
      } = parsePrometheusMetrics(text);
      const peersPerBucket = parsePerBucketMetrics(text);

      // Generate active buckets list (sorted by bucket number)
      const activeBuckets = [...peersPerBucket].sort(
        (a, b) => parseInt(a.bucket, 10) - parseInt(b.bucket, 10),
      );

      // Log the parsed metrics for debugging
      console.log("Parsed metrics result:", {
        knownPeers: parsedMetrics.bootnode_known_peers || 0,
        unreachablePeers: parsedMetrics.bootnode_unreachable_peers || 0,
        lastPollTimestamp: parsedMetrics.bootnode_last_poll_timestamp || 0,
        pollDuration: parsedMetrics.bootnode_poll_duration_seconds || 0,
        pollCount: parsedMetrics.bootnode_poll_count || 0,
        pollSuccess: parsedMetrics.bootnode_poll_success || 0,
        bucketsCount: peersPerBucket.length,
        peerDetailsCount: peerDetails.length,
        hasBootnodeInfo: !!bootnodeInfo,
      });

      // Update metrics state
      setMetrics({
        knownPeers: parsedMetrics.bootnode_known_peers || 0,
        unreachablePeers: parsedMetrics.bootnode_unreachable_peers || 0,
        lastPollTimestamp: parsedMetrics.bootnode_last_poll_timestamp || 0,
        pollDuration: parsedMetrics.bootnode_poll_duration_seconds || 0,
        pollCount: parsedMetrics.bootnode_poll_count || 0,
        pollSuccess: parsedMetrics.bootnode_poll_success || 0,
        peersPerBucket,
        activeBuckets,
        bootnodeInfo,
        peerDetails,
      });

      // Update historical time series data
      const timestamp = Date.now();
      const reachablePeers = peerDetails.filter((p) => p.reachable).length;
      const unreachablePeers = peerDetails.filter((p) => !p.reachable).length;
      const totalPeers = peerDetails.length;
      const reachabilityPercentage =
        totalPeers > 0 ? (reachablePeers / totalPeers) * 100 : 0;

      setHistoricalData((prevData) => {
        // Keep only the last 20 data points
        const newData = [
          ...prevData,
          {
            timestamp,
            totalPeers,
            reachablePeers,
            unreachablePeers,
            reachabilityPercentage,
          },
        ];

        if (newData.length > 20) {
          return newData.slice(newData.length - 20);
        }
        return newData;
      });

      // Update regional data
      // Group peers by region for regional growth tracking
      const regionCounts = {};
      peerDetails.forEach((peer) => {
        // Extract a "region" from the IP - this is a simplification
        // In a real implementation, you might use GeoIP data
        const ipParts = peer.ip.split(".");
        if (ipParts.length === 4) {
          const region = `${ipParts[0]}.${ipParts[1]}`;
          regionCounts[region] = (regionCounts[region] || 0) + 1;
        }
      });

      setHistoricalRegionalData((prevData) => {
        // Keep only the last 20 data points
        const newData = [
          ...prevData,
          {
            timestamp,
            regions: regionCounts,
          },
        ];

        if (newData.length > 20) {
          return newData.slice(newData.length - 20);
        }
        return newData;
      });

      // Generate graph data
      generateGraphData(
        parsedMetrics.bootnode_known_peers || 0,
        parsedMetrics.bootnode_unreachable_peers || 0,
        peersPerBucket,
        peerDetails,
        bootnodeInfo,
      );
    } catch (error) {
      console.error("Error fetching metrics:", error);
      if (error.name === "AbortError") {
        setError("Request timed out. The server may be unavailable.");
      } else {
        setError(`Failed to fetch metrics: ${error.message}`);
      }
      if (isPolling) {
        togglePolling(); // Stop polling on error
      }
    }
  };

  // Function to generate graph data for visualization
  const generateGraphData = (
    knownPeers,
    unreachablePeers,
    peersPerBucket,
    peerDetails,
    bootnodeInfo,
  ) => {
    console.log(
      `Generating graph data with known peers: ${knownPeers}, unreachable: ${unreachablePeers}, buckets: ${peersPerBucket.length}, peer details: ${peerDetails.length}`,
    );

    // Create bootnode with information from bootnode_info
    const nodes = [
      {
        id: "bootnode",
        name: "Bootnode",
        group: "bootnode",
        size: 20,
        ip: bootnodeInfo?.ip || "Unknown",
        nodeId: bootnodeInfo?.id || "Unknown",
        details: {
          connections: knownPeers,
        },
      },
    ];

    const links = [];

    // Create a map for fast bucket lookup
    const bucketNodes = {};

    // Generate nodes for each bucket
    peersPerBucket.forEach((bucket) => {
      const bucketId = `bucket-${bucket.bucket}`;

      // Calculate distance metric based on Kademlia XOR distance
      const distanceHex = (1n << BigInt(bucket.bucket)).toString(16);

      // Add bucket node if it doesn't exist
      if (!nodes.find((n) => n.id === bucketId)) {
        const bucketNode = {
          id: bucketId,
          name: `Bucket ${bucket.bucket}`,
          group: "bucket",
          bucket: bucket.bucket,
          size: 15,
          details: {
            distance: `0x${distanceHex}`,
            totalPeers: bucket.count,
            maxCapacity: 16, // K-bucket typical size
          },
        };

        nodes.push(bucketNode);
        bucketNodes[bucket.bucket] = bucketNode;

        // Link bootnode to bucket
        links.push({
          source: "bootnode",
          target: bucketId,
          value: 1.5, // Make these links stronger/thicker
        });
      }
    });

    // Add detailed peer information from bootnode_peer_details
    if (peerDetails.length > 0) {
      peerDetails.forEach((peerDetail, index) => {
        const bucketId = `bucket-${peerDetail.bucket}`;

        // Only add the peer if the bucket exists
        if (bucketNodes[peerDetail.bucket]) {
          // Determine the peer type based on in_iplist and reachable flags
          const group = peerDetail.inIplist
            ? "knownPeer"
            : peerDetail.reachable
              ? "reachable"
              : "unreachable";
          const size = peerDetail.inIplist ? 10 : 8;

          const peerId = `peer-${peerDetail.bucket}-${peerDetail.peerId.substring(0, 8)}`;

          nodes.push({
            id: peerId,
            name: `${group === "knownPeer" ? "Known Peer" : group === "reachable" ? "Reachable Peer" : "Unreachable Peer"}`,
            group: group,
            bucket: peerDetail.bucket,
            size: size,
            ip: peerDetail.ip,
            nodeId: peerDetail.peerId,
            isReachable: peerDetail.reachable,
            isInIplist: peerDetail.inIplist,
            firstSeenTime: peerDetail.firstSeenTime,
            timesReachable: peerDetail.timesReachable,
            timesSeenInScans: peerDetail.timesSeenInScans,
            timesUnreachable: peerDetail.timesUnreachable,
          });

          links.push({
            source: bucketId,
            target: peerId,
            value: peerDetail.inIplist ? 1.2 : peerDetail.reachable ? 1 : 0.5,
          });
        }
      });
    }

    console.log(
      `Generated graph with ${nodes.length} nodes and ${links.length} links`,
    );
    setGraphData({ nodes, links });
  };

  // Start or stop polling
  const togglePolling = () => {
    if (isPolling) {
      clearInterval(pollIntervalRef.current);
      setIsPolling(false);
      console.log("Polling stopped");
    } else {
      fetchMetrics(); // Fetch immediately
      pollIntervalRef.current = setInterval(fetchMetrics, pollInterval * 1000);
      setIsPolling(true);
      console.log(`Polling started with interval ${pollInterval} seconds`);
    }
  };

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        console.log("Polling stopped on component unmount");
      }
    };
  }, []);

  // Improved timestamp formatting
  const formatTimestamp = (timestamp) => {
    console.log("Formatting timestamp:", timestamp);

    // Handle invalid or zero timestamps
    if (!timestamp || timestamp <= 0) {
      return "No data";
    }

    try {
      // Convert scientific notation if needed
      const tsDate = new Date(timestamp * 1000);

      // Check if date is valid
      if (isNaN(tsDate.getTime())) {
        console.error("Invalid timestamp:", timestamp);
        return "Invalid date";
      }

      return tsDate.toLocaleString();
    } catch (error) {
      console.error("Error formatting timestamp:", error);
      return "Error";
    }
  };

  // Format first seen time
  const formatFirstSeen = (timestamp) => {
    if (!timestamp) return "";

    try {
      const date = new Date(timestamp * 1000);
      if (isNaN(date.getTime())) return "";

      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return `today (${date.toLocaleTimeString()})`;
      } else if (diffDays === 1) {
        return `yesterday (${date.toLocaleTimeString()})`;
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else {
        return date.toLocaleString();
      }
    } catch (error) {
      console.error("Error formatting first seen time:", error);
      return "";
    }
  };

  // Node color based on group with Ghibli palette
  const getNodeColor = (group) => {
    switch (group) {
      case "bootnode":
        return colors.bootnode;
      case "bucket":
        return colors.bucket;
      case "reachable":
        return colors.reachable;
      case "unreachable":
        return colors.unreachable;
      case "knownPeer":
        return colors.knownPeer;
      default:
        return "#999999";
    }
  };

  // Calculate link coordinates
  const renderLink = (link, index) => {
    const sourcePos = positions[link.source];
    const targetPos = positions[link.target];

    if (!sourcePos || !targetPos) return null;

    // Calculate if this link is connected to the hovered node
    const isHighlighted =
      hoveredNode &&
      (link.source === hoveredNode.id || link.target === hoveredNode.id);

    // Enhanced link with gradient and animation for active connections
    const sourceNode = graphData.nodes.find((n) => n.id === link.source);
    const targetNode = graphData.nodes.find((n) => n.id === link.target);

    // Get link color based on node types
    let linkColor;
    if (sourceNode && targetNode) {
      if (sourceNode.group === "bootnode" || targetNode.group === "bootnode") {
        linkColor = colors.bootnode;
      } else if (
        sourceNode.group === "knownPeer" ||
        targetNode.group === "knownPeer"
      ) {
        linkColor = colors.knownPeer;
      } else if (
        sourceNode.group === "unreachable" ||
        targetNode.group === "unreachable"
      ) {
        linkColor = colors.unreachable;
      } else if (
        sourceNode.group === "bucket" ||
        targetNode.group === "bucket"
      ) {
        linkColor = colors.bucket;
      } else {
        linkColor = colors.reachable;
      }
    } else {
      linkColor = "#ffffff";
    }

    // Create a unique ID for this link's gradient
    const gradientId = `linkGradient-${index}`;

    // Determine if link should be dashed (for unreachable peers)
    const isDashed =
      sourceNode?.group === "unreachable" ||
      targetNode?.group === "unreachable";

    return (
      <g key={`link-${index}`}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={linkColor} stopOpacity="0.8" />
            <stop offset="100%" stopColor={linkColor} stopOpacity="0.2" />
          </linearGradient>
        </defs>

        <line
          x1={sourcePos.x}
          y1={sourcePos.y}
          x2={targetPos.x}
          y2={targetPos.y}
          stroke={isHighlighted ? linkColor : `url(#${gradientId})`}
          strokeOpacity={isHighlighted ? 0.8 : 0.3}
          strokeWidth={isHighlighted ? link.value * 3 : link.value * 2}
          strokeLinecap="round"
          strokeDasharray={isDashed ? "4 2" : ""}
        />

        {/* Add animated particle effect for active connections */}
        {link.value > 0.7 && !isDashed && (
          <circle r="2" fill={linkColor} opacity="0.7" filter="url(#glow)">
            <animateMotion
              dur={`${3 + Math.random() * 2}s`}
              repeatCount="indefinite"
              path={`M${sourcePos.x},${sourcePos.y} L${targetPos.x},${targetPos.y}`}
            />
          </circle>
        )}
      </g>
    );
  };

  // Node hover state
  const [hoveredNode, setHoveredNode] = useState(null);

  // Render tooltip
  const renderTooltip = () => {
    if (!hoveredNode) return null;

    const pos = positions[hoveredNode.id];
    if (!pos) return null;

    // Calculate tooltip position, accounting for zoom and pan
    // This is crucial - we need to transform from graph coordinates to screen coordinates
    const svgRect = svgRef.current?.getBoundingClientRect();

    if (!svgRect) return null;

    // Calculate viewport dimensions based on zoom
    const viewWidth = graphWidth / zoom;
    const viewHeight = graphHeight / zoom;

    // Calculate the graph coordinates to screen coordinates ratio
    const scaleX = svgRect.width / viewWidth;
    const scaleY = svgRect.height / viewHeight;

    // Calculate the position in screen coordinates
    const screenX = (pos.x - (pan.x - viewWidth / 2)) * scaleX + svgRect.left;
    const screenY = (pos.y - (pan.y - viewHeight / 2)) * scaleY + svgRect.top;

    // Determine tooltip content based on node type
    let tooltipContent = "";

    switch (hoveredNode.group) {
      case "bootnode":
        tooltipContent = (
          <div>
            <div>Bootnode</div>
            <div className="mt-1 text-xs font-normal">IP: {hoveredNode.ip}</div>
            <div className="text-xs font-normal">
              ID: {hoveredNode.nodeId.substring(0, 12)}...
            </div>
          </div>
        );
        break;
      case "bucket":
        tooltipContent = (
          <div>
            <div>Bucket: {hoveredNode.bucket}</div>
            <div className="mt-1 text-xs font-normal">
              Peers: {hoveredNode.details.totalPeers}
            </div>
            <div className="text-xs font-normal">
              Distance: {hoveredNode.details.distance}
            </div>
          </div>
        );
        break;
      case "reachable":
        tooltipContent = (
          <div>
            <div>Reachable Peer</div>
            <div className="mt-1 text-xs font-normal">IP: {hoveredNode.ip}</div>
            <div className="text-xs font-normal">
              ID: {hoveredNode.nodeId.substring(0, 12)}...
            </div>
            <div className="text-xs font-normal">
              Bucket: {hoveredNode.bucket}
            </div>
            {hoveredNode.firstSeenTime && (
              <div className="text-xs font-normal">
                First seen: {formatFirstSeen(hoveredNode.firstSeenTime)}
              </div>
            )}
            {hoveredNode.timesSeenInScans > 0 && (
              <div className="text-xs font-normal">
                Reliability: {hoveredNode.timesReachable}/
                {hoveredNode.timesSeenInScans} (
                {Math.round(
                  (hoveredNode.timesReachable / hoveredNode.timesSeenInScans) *
                    100,
                )}
                %)
              </div>
            )}
          </div>
        );
        break;
      case "unreachable":
        tooltipContent = (
          <div>
            <div>Unreachable Peer</div>
            <div className="mt-1 text-xs font-normal">IP: {hoveredNode.ip}</div>
            <div className="text-xs font-normal">
              ID: {hoveredNode.nodeId.substring(0, 12)}...
            </div>
            <div className="text-xs font-normal">
              Bucket: {hoveredNode.bucket}
            </div>
            {hoveredNode.firstSeenTime && (
              <div className="text-xs font-normal">
                First seen: {formatFirstSeen(hoveredNode.firstSeenTime)}
              </div>
            )}
            {hoveredNode.timesSeenInScans > 0 && (
              <div className="text-xs font-normal">
                Reliability: {hoveredNode.timesReachable}/
                {hoveredNode.timesSeenInScans} (
                {Math.round(
                  (hoveredNode.timesReachable / hoveredNode.timesSeenInScans) *
                    100,
                )}
                %)
              </div>
            )}
          </div>
        );
        break;
      case "knownPeer":
        tooltipContent = (
          <div>
            <div>Known Peer</div>
            <div className="mt-1 text-xs font-normal">IP: {hoveredNode.ip}</div>
            <div className="text-xs font-normal">
              ID: {hoveredNode.nodeId.substring(0, 12)}...
            </div>
            <div className="text-xs font-normal">
              Bucket: {hoveredNode.bucket}
            </div>
            {hoveredNode.firstSeenTime && (
              <div className="text-xs font-normal">
                First seen: {formatFirstSeen(hoveredNode.firstSeenTime)}
              </div>
            )}
            {hoveredNode.timesSeenInScans > 0 && (
              <div className="text-xs font-normal">
                Reliability: {hoveredNode.timesReachable}/
                {hoveredNode.timesSeenInScans} (
                {Math.round(
                  (hoveredNode.timesReachable / hoveredNode.timesSeenInScans) *
                    100,
                )}
                %)
              </div>
            )}
          </div>
        );
        break;
      default:
        tooltipContent = hoveredNode.name;
    }

    return (
      <div
        style={{
          position: "fixed",
          left: screenX + 15, // Offset from node
          top: screenY - 30,
          backgroundColor: colors.cardBg,
          color: colors.text,
          borderRadius: "4px",
          padding: "8px 12px",
          border: `2px solid ${getNodeColor(hoveredNode.group)}`,
          fontSize: "14px",
          fontWeight: "bold",
          maxWidth: "250px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
          zIndex: 1000,
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}
      >
        {tooltipContent}
      </div>
    );
  };

  // Render nodes
  const renderNode = (node) => {
    const pos = positions[node.id];
    if (!pos) return null;

    // Determine if this node is being hovered
    const isHovered = hoveredNode && hoveredNode.id === node.id;

    // Create a glow effect for hovered nodes
    const glowEffect = isHovered ? (
      <circle
        r={node.size + 5}
        fill="none"
        stroke={getNodeColor(node.group)}
        strokeWidth="2"
        opacity="0.5"
        filter="url(#glow)"
      />
    ) : null;

    // Determine node appearance based on type
    let nodeContent;

    if (node.group === "bootnode") {
      // Star shape for bootnode
      nodeContent = (
        <polygon
          points="0,-20 4.7,-8.1 17.6,-6.2 8.8,3.3 10.9,16.2 0,10 -10.9,16.2 -8.8,3.3 -17.6,-6.2 -4.7,-8.1"
          fill={getNodeColor(node.group)}
          stroke="#fff"
          strokeWidth="1"
          opacity={isHovered ? 1 : 0.9}
        />
      );
    } else if (node.group === "bucket") {
      // Diamond shape for buckets
      nodeContent = (
        <rect
          x={-node.size}
          y={-node.size}
          width={node.size * 2}
          height={node.size * 2}
          fill={getNodeColor(node.group)}
          stroke="#fff"
          strokeWidth="1"
          opacity={isHovered ? 1 : 0.9}
          transform="rotate(45)"
        />
      );
    } else if (node.group === "knownPeer") {
      // Hexagon shape for known peers
      const hexPoints = [];
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i;
        const x = node.size * Math.cos(angle);
        const y = node.size * Math.sin(angle);
        hexPoints.push(`${x},${y}`);
      }

      nodeContent = (
        <>
          <polygon
            points={hexPoints.join(" ")}
            fill={getNodeColor(node.group)}
            stroke="#fff"
            strokeWidth={isHovered ? "1.5" : "0.5"}
            opacity={isHovered ? 1 : 0.9}
          />
          {isHovered && (
            <circle r={node.size * 0.5} fill="#fff" opacity="0.7" />
          )}
        </>
      );
    } else if (node.group === "unreachable") {
      // X shape for unreachable peers
      nodeContent = (
        <>
          <circle
            r={node.size}
            fill={getNodeColor(node.group)}
            opacity={isHovered ? 0.9 : 0.7}
            stroke="#fff"
            strokeWidth={isHovered ? "1.5" : "0.5"}
          />
          <line
            x1={-node.size * 0.6}
            y1={-node.size * 0.6}
            x2={node.size * 0.6}
            y2={node.size * 0.6}
            stroke="#fff"
            strokeWidth="1.5"
          />
          <line
            x1={node.size * 0.6}
            y1={-node.size * 0.6}
            x2={-node.size * 0.6}
            y2={node.size * 0.6}
            stroke="#fff"
            strokeWidth="1.5"
          />
        </>
      );
    } else {
      // Circle for reachable peers with pulse
      const pulseEffect = (
        <circle
          r={node.size}
          fill={getNodeColor(node.group)}
          opacity="0.2"
          className={isHovered ? "pulse-fast" : "pulse-slow"}
        />
      );

      nodeContent = (
        <>
          {pulseEffect}
          <circle
            r={node.size}
            fill={getNodeColor(node.group)}
            stroke="#fff"
            strokeWidth={isHovered ? "1.5" : "0.5"}
            opacity={isHovered ? 1 : 0.9}
          />
        </>
      );
    }

    return (
      <g
        key={node.id}
        transform={`translate(${pos.x},${pos.y})`}
        onMouseEnter={() => setHoveredNode(node)}
        onMouseLeave={() => setHoveredNode(null)}
        style={{ cursor: "pointer" }}
      >
        {glowEffect}
        {nodeContent}
      </g>
    );
  };

  // Test the Prometheus connection
  const testConnection = async () => {
    try {
      setError(null);
      console.log(`Testing connection to: ${prometheusUrl}/metrics`);

      // Create an AbortController for the timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10-second timeout

      const response = await fetch(`${prometheusUrl}/metrics`, {
        method: "GET",
        mode: "cors",
        credentials: "omit",
        signal: controller.signal,
        headers: {
          Accept: "text/plain",
          "Cache-Control": "no-cache",
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const text = await response.text();
      console.log(
        `Connection test successful! Received data length: ${text.length}`,
      );

      const metrics = parsePrometheusMetrics(text);
      alert(
        `Connection successful! Found ${metrics.bootnode_known_peers || 0} known peers.`,
      );
    } catch (error) {
      console.error("Connection test error:", error);
      if (error.name === "AbortError") {
        setError("Connection test timed out. The server may be unavailable.");
      } else {
        setError(`Connection test failed: ${error.message}`);
      }
    }
  };

  // Helper function to create a grid of bucket tiles
  const renderBucketTiles = () => {
    if (!metrics.activeBuckets || metrics.activeBuckets.length === 0) {
      return (
        <div
          className="text-center py-8"
          style={{ color: colors.textSecondary }}
        >
          No buckets data available
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2 max-h-64 overflow-y-auto p-2">
        {metrics.activeBuckets.map((bucket) => (
          <div
            key={`bucket-${bucket.bucket}`}
            className="p-2 rounded text-center"
            style={{ backgroundColor: colors.inputBg }}
          >
            <div className="text-xs" style={{ color: colors.textSecondary }}>
              Bucket
            </div>
            <div className="text-lg font-bold" style={{ color: colors.text }}>
              {bucket.bucket}
            </div>
            <div className="text-sm">
              <span style={{ color: colors.accent }}>{bucket.count}</span> peers
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Generate data for bucket bar chart
  const bucketChartData = metrics.activeBuckets.map((bucket) => ({
    name: `Bucket ${bucket.bucket}`,
    peers: bucket.count,
    fill: colors.bucket,
  }));

  return (
    <div
      style={{ backgroundColor: colors.background }}
      className="text-white min-h-screen p-6"
    >
      <h1 className="text-3xl font-bold mb-6" style={{ color: colors.text }}>
        Flashcaps🛰️
      </h1>

      {/* Controls */}
      <div
        style={{ backgroundColor: colors.cardBg }}
        className="p-4 rounded-lg mb-6 flex flex-wrap gap-4 items-center"
      >
        <div>
          <label
            className="block text-sm mb-1"
            style={{ color: colors.textSecondary }}
          >
            Prometheus URL:
          </label>
          <input
            type="text"
            value={prometheusUrl}
            onChange={(e) => setPrometheusUrl(e.target.value)}
            style={{ backgroundColor: colors.inputBg }}
            className="text-white px-3 py-2 rounded w-64"
            placeholder="https://eth-map.deltaflyer.llc"
          />
        </div>

        <div>
          <label
            className="block text-sm mb-1"
            style={{ color: colors.textSecondary }}
          >
            Poll Interval (seconds):
          </label>
          <input
            type="number"
            value={pollInterval}
            onChange={(e) => setPollInterval(parseInt(e.target.value) || 10)}
            min="1"
            style={{ backgroundColor: colors.inputBg }}
            className="text-white px-3 py-2 rounded w-24"
          />
        </div>

        <button
          onClick={testConnection}
          style={{ backgroundColor: colors.accent }}
          className="px-4 py-2 rounded font-medium hover:bg-opacity-90 text-gray-900"
        >
          Test Connection
        </button>

        <button
          onClick={togglePolling}
          style={{
            backgroundColor: isPolling ? colors.error : colors.success,
          }}
          className="px-4 py-2 rounded font-medium hover:bg-opacity-90 text-gray-900"
        >
          {isPolling ? "Stop Polling" : "Start Polling"}
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div
          style={{ backgroundColor: colors.error }}
          className="text-white p-4 rounded-lg mb-6 flex items-center"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {/* Connection help */}
      <div
        style={{ backgroundColor: colors.accent, color: "#1D3557" }}
        className="p-4 rounded-lg mb-6"
      >
        <h3 className="text-lg font-semibold mb-2">
          Prometheus Metrics Endpoint
        </h3>
        <p className="mb-2">
          Test if the flashcaps Prometheus metrics endpoint is working
          correctly, try accessing this URL directly:
        </p>
        <ul className="list-disc list-inside space-y-1 ml-4">
          <li>
            <code
              style={{ backgroundColor: "rgba(42, 67, 101, 0.5)" }}
              className="px-2 py-1 rounded"
            >
              {prometheusUrl || "https://eth-map.deltaflyer.llc"}/metrics
            </code>
          </li>
        </ul>
        <p className="mt-2">
          Make sure the revere proxy server is correctly configured to proxy
          this request to the actual metrics endpoint.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div
          style={{ backgroundColor: colors.cardBg }}
          className="p-4 rounded-lg"
        >
          <h2
            className="text-xl font-semibold mb-2"
            style={{ color: colors.text }}
          >
            Network Overview
          </h2>
          <div className="grid grid-cols-2 gap-2">
            <div
              style={{ backgroundColor: colors.inputBg }}
              className="p-3 rounded"
            >
              <div style={{ color: colors.textSecondary }} className="text-sm">
                Known Peers
              </div>
              <div
                style={{ color: colors.text }}
                className="text-2xl font-bold"
              >
                {metrics.knownPeers}
              </div>
            </div>
            <div
              style={{ backgroundColor: colors.inputBg }}
              className="p-3 rounded"
            >
              <div style={{ color: colors.textSecondary }} className="text-sm">
                Unreachable Peers
              </div>
              <div
                style={{ color: colors.text }}
                className="text-2xl font-bold"
              >
                {metrics.unreachablePeers}
              </div>
            </div>
            <div
              style={{ backgroundColor: colors.inputBg }}
              className="p-3 rounded"
            >
              <div style={{ color: colors.textSecondary }} className="text-sm">
                Poll Count
              </div>
              <div
                style={{ color: colors.text }}
                className="text-2xl font-bold"
              >
                {metrics.pollCount}
              </div>
            </div>
            <div
              style={{ backgroundColor: colors.inputBg }}
              className="p-3 rounded"
            >
              <div style={{ color: colors.textSecondary }} className="text-sm">
                Poll Status
              </div>
              <div
                style={{
                  color: metrics.pollSuccess ? colors.success : colors.error,
                }}
                className="text-2xl font-bold"
              >
                {metrics.pollSuccess ? "Success" : "Failed"}
              </div>
            </div>
          </div>
        </div>

        <div
          style={{ backgroundColor: colors.cardBg }}
          className="p-4 rounded-lg"
        >
          <h2
            className="text-xl font-semibold mb-2"
            style={{ color: colors.text }}
          >
            Last Poll Details
          </h2>
          <div className="space-y-2">
            <div
              style={{ backgroundColor: colors.inputBg }}
              className="p-3 rounded"
            >
              <div style={{ color: colors.textSecondary }} className="text-sm">
                Timestamp
              </div>
              <div
                style={{ color: colors.text }}
                className="text-lg font-medium"
              >
                {formatTimestamp(metrics.lastPollTimestamp)}
              </div>
            </div>
            <div
              style={{ backgroundColor: colors.inputBg }}
              className="p-3 rounded"
            >
              <div style={{ color: colors.textSecondary }} className="text-sm">
                Duration
              </div>
              <div
                style={{ color: colors.text }}
                className="text-lg font-medium"
              >
                {metrics.pollDuration.toFixed(2)} seconds
              </div>
            </div>
          </div>
        </div>

        <div
          style={{ backgroundColor: colors.cardBg }}
          className="p-4 rounded-lg md:col-span-2 lg:col-span-1"
        >
          <h2 className="text-xl font-semibold mb-2 flex justify-between items-center">
            <span style={{ color: colors.text }}>Kademlia Buckets</span>
            <span
              style={{ color: colors.textSecondary }}
              className="text-sm font-normal"
            >
              {metrics.activeBuckets.length} active buckets
            </span>
          </h2>
          {renderBucketTiles()}
        </div>
      </div>

      {/* NEW COMPONENTS - Peer Type Distribution and Time Series */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Peer Type Distribution Chart */}
        <div
          style={{ backgroundColor: colors.cardBg }}
          className="p-4 rounded-lg"
        >
          <h2
            className="text-xl font-semibold mb-2"
            style={{ color: colors.text }}
          >
            Peer Type Distribution
          </h2>
          <PeerTypeDistribution
            peerDetails={metrics.peerDetails}
            colors={colors}
          />
        </div>

        {/* Time Series Graphs */}
        <div
          style={{ backgroundColor: colors.cardBg }}
          className="p-4 rounded-lg lg:col-span-2"
        >
          <h2
            className="text-xl font-semibold mb-2"
            style={{ color: colors.text }}
          >
            Network Metrics Over Time
          </h2>
          <TimeSeriesGraph historicalData={historicalData} colors={colors} />
        </div>
      </div>

      {/* Visualization Mode Toggles */}
      <div
        style={{ backgroundColor: colors.cardBg }}
        className="p-4 rounded-lg mb-6"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold" style={{ color: colors.text }}>
            P2P Network Visualization
          </h2>
          <div className="flex bg-gray-800 rounded overflow-hidden">
            <button
              onClick={() => setVisualizationMode("network")}
              className={`px-4 py-2 font-medium ${
                visualizationMode === "network"
                  ? "bg-blue-600 text-white"
                  : "text-gray-400 hover:bg-gray-700"
              }`}
            >
              Network Graph
            </button>
            <button
              onClick={() => setVisualizationMode("geographic")}
              className={`px-4 py-2 font-medium ${
                visualizationMode === "geographic"
                  ? "bg-blue-600 text-white"
                  : "text-gray-400 hover:bg-gray-700"
              }`}
            >
              Geographic Map
            </button>
          </div>
        </div>

        {/* Network Graph View */}
        {visualizationMode === "network" && (
          <>
            <div className="mb-2 flex justify-end space-x-2">
              <button
                onClick={handleZoomIn}
                style={{ backgroundColor: colors.accent }}
                className="px-3 py-1 rounded hover:bg-opacity-90 text-gray-900 flex items-center"
                title="Zoom In"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
              </button>
              <button
                onClick={handleZoomOut}
                style={{ backgroundColor: colors.accent }}
                className="px-3 py-1 rounded hover:bg-opacity-90 text-gray-900 flex items-center"
                title="Zoom Out"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18 12H6"
                  />
                </svg>
              </button>
              <button
                onClick={handleZoomReset}
                style={{ backgroundColor: colors.accent }}
                className="px-3 py-1 rounded hover:bg-opacity-90 text-gray-900 flex items-center"
                title="Reset View"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>
            </div>
            <div
              className="rounded relative overflow-hidden cursor-move"
              style={{
                borderColor: colors.inputBg,
                borderWidth: "1px",
                height: "600px",
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onWheel={handleWheel}
              ref={svgRef}
            >
              <svg
                width="100%"
                height="100%"
                viewBox={calculateViewBox()}
                preserveAspectRatio="xMidYMid meet"
                style={{ background: colors.background }}
              >
                <defs>
                  <filter
                    id="glow"
                    x="-50%"
                    y="-50%"
                    width="200%"
                    height="200%"
                  >
                    <feGaussianBlur stdDeviation="2.5" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                  <radialGradient
                    id="nodeGradient"
                    cx="50%"
                    cy="50%"
                    r="50%"
                    fx="50%"
                    fy="50%"
                  >
                    <stop offset="0%" stopColor="white" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="white" stopOpacity="0" />
                  </radialGradient>

                  {/* Animation definitions */}
                  <style type="text/css">
                    {`
                      @keyframes pulse-slow {
                        0% { r: 6; opacity: 0.2; }
                        50% { r: 12; opacity: 0.1; }
                        100% { r: 6; opacity: 0.2; }
                      }

                      @keyframes pulse-fast {
                        0% { r: 6; opacity: 0.3; }
                        50% { r: 15; opacity: 0.1; }
                        100% { r: 6; opacity: 0.3; }
                      }

                      .pulse-slow {
                        animation: pulse-slow 4s infinite ease-in-out;
                      }

                      .pulse-fast {
                        animation: pulse-fast 1.5s infinite ease-in-out;
                      }
                    `}
                  </style>
                </defs>

                <g>
                  {graphData.links.map(renderLink)}
                  {graphData.nodes.map(renderNode)}
                </g>
              </svg>
              {renderTooltip()}
              <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 rounded px-2 py-1 text-sm">
                Zoom: {(zoom * 100).toFixed(0)}%
              </div>
            </div>
          </>
        )}

        {/* Geographic Map View */}
        {visualizationMode === "geographic" && (
          <GeographicMap peerDetails={metrics.peerDetails} colors={colors} />
        )}
      </div>

      <div
        style={{ backgroundColor: colors.cardBg }}
        className="p-4 rounded-lg mt-6"
      >
        <h2
          className="text-xl font-semibold mb-2"
          style={{ color: colors.text }}
        >
          Legend
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="flex items-center">
            <div
              className="w-5 h-5 rounded-full mr-2"
              style={{ backgroundColor: colors.bootnode }}
            ></div>
            <span style={{ color: colors.text }}>Bootnode</span>
          </div>
          <div className="flex items-center">
            <div
              className="w-5 h-5 rounded-full mr-2"
              style={{ backgroundColor: colors.bucket }}
            ></div>
            <span style={{ color: colors.text }}>Kademlia Bucket</span>
          </div>
          <div className="flex items-center">
            <div
              className="w-5 h-5 rounded-full mr-2"
              style={{ backgroundColor: colors.reachable }}
            ></div>
            <span style={{ color: colors.text }}>Reachable Peer</span>
          </div>
          <div className="flex items-center">
            <div
              className="w-5 h-5 rounded-full mr-2"
              style={{ backgroundColor: colors.unreachable }}
            ></div>
            <span style={{ color: colors.text }}>Unreachable Peer</span>
          </div>
          <div className="flex items-center">
            <div
              className="w-5 h-5 rounded-full mr-2"
              style={{ backgroundColor: colors.knownPeer }}
            ></div>
            <span style={{ color: colors.text }}>Known Peer</span>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div
        style={{ backgroundColor: colors.cardBg }}
        className="p-4 rounded-lg mt-6"
      >
        <h2
          className="text-xl font-semibold mb-2"
          style={{ color: colors.text }}
        >
          Peer History Stats
        </h2>
        <div className="overflow-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th
                  className="p-2 text-left"
                  style={{ color: colors.textSecondary }}
                >
                  Type
                </th>
                <th
                  className="p-2 text-left"
                  style={{ color: colors.textSecondary }}
                >
                  Total Count
                </th>
                <th
                  className="p-2 text-left"
                  style={{ color: colors.textSecondary }}
                >
                  Average Reliability
                </th>
                <th
                  className="p-2 text-left"
                  style={{ color: colors.textSecondary }}
                >
                  Consistently Online
                </th>
                <th
                  className="p-2 text-left"
                  style={{ color: colors.textSecondary }}
                >
                  Consistently Offline
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Calculate statistics from peer details */}
              {(() => {
                // Skip if no peer details
                if (!metrics.peerDetails || metrics.peerDetails.length === 0) {
                  return (
                    <tr>
                      <td
                        colSpan="5"
                        className="p-2 text-center"
                        style={{ color: colors.textSecondary }}
                      >
                        No peer history data available
                      </td>
                    </tr>
                  );
                }

                // Calculate statistics
                const totalPeers = metrics.peerDetails.length;
                const multipleScans = metrics.peerDetails.filter(
                  (p) => p.timesSeenInScans > 1,
                ).length;
                const reliablePeers = metrics.peerDetails.filter(
                  (p) =>
                    p.timesSeenInScans > 1 &&
                    p.timesReachable / p.timesSeenInScans >= 0.8,
                ).length;
                const unreliablePeers = metrics.peerDetails.filter(
                  (p) =>
                    p.timesSeenInScans > 1 &&
                    p.timesReachable / p.timesSeenInScans <= 0.2,
                ).length;

                // Calculate average reliability
                let avgReliability = 0;
                const peersWithHistory = metrics.peerDetails.filter(
                  (p) => p.timesSeenInScans > 0,
                );
                if (peersWithHistory.length > 0) {
                  const totalReliability = peersWithHistory.reduce(
                    (sum, peer) =>
                      sum + peer.timesReachable / peer.timesSeenInScans,
                    0,
                  );
                  avgReliability =
                    (totalReliability / peersWithHistory.length) * 100;
                }

                return (
                  <tr>
                    <td className="p-2" style={{ color: colors.text }}>
                      All Peers
                    </td>
                    <td className="p-2" style={{ color: colors.text }}>
                      {totalPeers}
                    </td>
                    <td className="p-2" style={{ color: colors.text }}>
                      {avgReliability.toFixed(1)}%
                    </td>
                    <td className="p-2" style={{ color: colors.reachable }}>
                      {reliablePeers}
                    </td>
                    <td className="p-2" style={{ color: colors.unreachable }}>
                      {unreliablePeers}
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default P2PVisualizer;
